import type { HoldCallResponse, SoftphoneDevice, VoiceTokenResponse } from '@mevaser/shared'
import { useQueryClient } from '@tanstack/react-query'
import type * as React from 'react'
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { api } from '../../lib/api.js'
import { useAuth } from '../../lib/auth.js'
import {
  closeIncomingCallNotification,
  showIncomingCallNotification,
} from '../../lib/call-notification.js'
import { callerNotificationLabel } from './caller-name.js'

/** Loaded lazily (see below) — the shape we actually touch, not the full SDK. */
type TwilioCall = {
  parameters: Record<string, string>
  customParameters?: Map<string, string>
  on: (event: string, handler: (...args: unknown[]) => void) => void
  accept: () => void
  reject: () => void
  ignore: () => void
  disconnect: () => void
  mute: (muted: boolean) => void
  isMuted: () => boolean
}
type TwilioDevice = {
  on: (event: string, handler: (...args: unknown[]) => void) => void
  register: () => Promise<void>
  unregister: () => Promise<void>
  updateToken: (token: string) => void
  connect: (options: { params: Record<string, string> }) => Promise<TwilioCall>
  destroy: () => void
}

export type SoftphoneState = 'idle' | 'connecting' | 'ringing' | 'active' | 'held'

export interface WaitingCall {
  remoteNumber: string
  fromOtherDevice: boolean
}

interface SoftphoneContextValue {
  /** Whether browser calling is available at all for this staff member —
   *  false while loading, and permanently false when voice isn't configured
   *  or this account lacks `support:write`. Callers use this to hide UI
   *  rather than showing a button that will only ever fail. */
  available: boolean
  state: SoftphoneState
  /** The other party's number, while ringing or on a call. */
  remoteNumber: string | null
  /** True while ringing for a call this staff member started on another of
   *  their own browsers (t1229), rather than an inbound customer call. */
  ringingFromOtherDevice: boolean
  /** The signed-in staff member's other browsers that are online right now. */
  otherDevices: SoftphoneDevice[]
  muted: boolean
  /** Whether this browser rings for incoming customer calls (t1239). Saved
   *  per browser; the other browsers of the same staff member are unaffected. */
  ringHere: boolean
  setRingHere: (on: boolean) => void
  /** True while the browser is holding back this page's sound (t1291) — iPad
   *  Safari keeps audio locked until a tap, so a call would ring silently. */
  soundBlocked: boolean
  /** This browser's friendly name, e.g. "iPad · Safari". */
  thisDeviceLabel: string
  /** A second customer call ringing while this browser is already on a call or
   *  has a caller on hold (t1300). Answer it, or decline to send it onward. */
  waiting: WaitingCall | null
  /** The number of the caller parked on hold, while another call is live. */
  parkedNumber: string | null
  /** Answer the waiting call. 'hold' parks the live call first, 'end' hangs it
   *  up; with nothing live both simply answer (a parked caller stays parked). */
  answerWaiting: (mode: 'hold' | 'end') => Promise<void>
  declineWaiting: () => void
  placeCall: (to: string) => Promise<void>
  /** t1404: hear a phone-menu key spoken in the voice callers hear. Not a real
   *  call: the call bar never shows it. Rejects if a call is live. */
  playIvrKeyPreview: (
    key:
      | { digit: string; label: string; message: string | null }
      // t1405: or a phone-voice sample, in a voice that may not be saved yet.
      | { voice: string },
    onError?: (message: string) => void,
  ) => Promise<void>
  stopIvrKeyPreview: () => void
  previewing: boolean
  /** Ring one of `otherDevices`; the customer is dialed once it answers. */
  placeCallOnDevice: (to: string, deviceKey: string) => Promise<void>
  answer: () => void
  reject: () => void
  /** Stop ringing on this browser only; the call keeps ringing elsewhere. */
  silence: () => void
  hangUp: () => void
  toggleMute: () => void
  /** Put the caller on hold (t1228); this browser's own leg drops until
   *  `resume`. Rejects with the API error if the caller is already gone. */
  hold: () => Promise<void>
  resume: () => Promise<void>
  /** Hand the live or held call to a Support-settings call target. */
  transfer: (targetId: string, announce: boolean) => Promise<void>
}

const SoftphoneContext = createContext<SoftphoneContextValue | null>(null)

export function useSoftphone(): SoftphoneContextValue {
  const ctx = useContext(SoftphoneContext)
  if (!ctx) throw new Error('useSoftphone must be used inside a SoftphoneProvider')
  return ctx
}

// Module scope, not a ref: a Twilio Device holds a live signaling connection
// that must not be torn down and recreated on every StrictMode double-mount
// or route change — same reasoning as SupportWidget's `injected` flag.
let device: TwilioDevice | null = null
let deviceIdentity: string | null = null
// t1406: a connect-only Device for the menu-key preview. It is never
// registered (so it can't ring for customer calls) and feeds the call a silent
// stream instead of opening the microphone, so a computer with no mic can
// still listen to a key.
let previewDevice: TwilioDevice | null = null
let previewIdentity: string | null = null

// The Voice SDK plays its ringtone through one shared AudioContext and never
// resumes it. Safari starts it suspended and only a tap can unlock it (t1291),
// so we keep hold of it, resume it on any gesture, and report when it's locked.
let audioCtx: AudioContext | null = null
const audioListeners = new Set<() => void>()

function trackAudioContext(ctx: AudioContext | undefined) {
  if (!ctx || ctx === audioCtx) return
  audioCtx = ctx
  ctx.addEventListener('statechange', () => {
    for (const notify of audioListeners) notify()
  })
  for (const notify of audioListeners) notify()
}

function unlockAudio() {
  if (audioCtx && audioCtx.state !== 'running') void audioCtx.resume().catch(() => {})
}

const HEARTBEAT_MS = 45_000
const DEVICE_STORAGE_KEY = 'mevaser.softphone.device'
const RING_STORAGE_KEY = 'mevaser.softphone.ringIncoming'

function readRingHere(): boolean {
  try {
    return localStorage.getItem(RING_STORAGE_KEY) !== 'false'
  } catch {
    return true
  }
}

/** "iPad · Safari", "Windows · Chrome" — enough for a staff member to tell
 *  their own browsers apart in the call menu. */
function describeBrowser(): string {
  const ua = navigator.userAgent
  // iPadOS Safari reports itself as a Mac; only touch support gives it away.
  const isIpad = /iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  const os = isIpad
    ? 'iPad'
    : /iPhone/.test(ua)
      ? 'iPhone'
      : /Android/.test(ua)
        ? 'Android'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Mac/.test(ua)
            ? 'Mac'
            : /Linux/.test(ua)
              ? 'Linux'
              : 'Browser'
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome|CriOS/.test(ua)
      ? 'Chrome'
      : /Firefox|FxiOS/.test(ua)
        ? 'Firefox'
        : /Safari/.test(ua)
          ? 'Safari'
          : 'browser'
  return `${os} · ${browser}`
}

function randomDeviceKey(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(12))
  return Array.from(bytes, (b) => b.toString(36).padStart(2, '0'))
    .join('')
    .slice(0, 24)
}

/** This browser's stable id for call routing (t1229). Kept in localStorage so
 *  a reload is the same device; falls back to a per-load key when storage is
 *  blocked, which just means it re-registers as a new device each load. */
let cachedDevice: { key: string; label: string } | null = null
function thisDevice(): { key: string; label: string } {
  if (cachedDevice) return cachedDevice
  const label = describeBrowser()
  let key: string | null = null
  try {
    key = localStorage.getItem(DEVICE_STORAGE_KEY)
    if (!key || !/^[a-z0-9]{16,32}$/.test(key)) {
      key = randomDeviceKey()
      localStorage.setItem(DEVICE_STORAGE_KEY, key)
    }
  } catch {
    key = key ?? randomDeviceKey()
  }
  cachedDevice = { key, label }
  return cachedDevice
}

/**
 * A Device's access token lasts an hour; Twilio warns shortly before it ends.
 * Every Device must be given this, however it was created — one without it
 * keeps its dead token and throws AccessTokenExpired / connection errors from
 * then on (31005, 31009, 20104).
 */
function refreshTokenBeforeExpiry(target: TwilioDevice): void {
  target.on('tokenWillExpire', () => {
    void api
      .post<VoiceTokenResponse>('/support/voice/token', {})
      .then((fresh) => target.updateToken(fresh.token))
      .catch(() => {
        // Session likely expired — let registration lapse quietly; an
        // active call keeps its media until it ends regardless.
      })
  })
}

/**
 * The admin dashboard's browser softphone (t0869) — a Twilio Voice Device,
 * mounted once for a signed-in staff member (see AppShell), that can place
 * outbound calls and, when this staff member has an active "browser" ring
 * target (Support settings), receive inbound ones too.
 *
 * Silent by default when unavailable: an account without `support:write`,
 * or a shop that hasn't set up browser calling under Integrations, simply
 * sees no call controls anywhere — same failure philosophy as SupportWidget.
 */
export function SoftphoneProvider({ children }: { children: React.ReactNode }) {
  const { staff, can } = useAuth()
  const eligible = Boolean(staff) && can('support:write')

  const [available, setAvailable] = useState(false)
  const [state, setState] = useState<SoftphoneState>('idle')
  const [remoteNumber, setRemoteNumber] = useState<string | null>(null)
  const [muted, setMuted] = useState(false)
  const [ringHere, setRingHereState] = useState(readRingHere)
  const ringHereRef = useRef(ringHere)
  const [soundBlocked, setSoundBlocked] = useState(false)
  const beatRef = useRef<(() => void) | null>(null)
  const [ringingFromOtherDevice, setRingingFromOtherDevice] = useState(false)
  const [otherDevices, setOtherDevices] = useState<SoftphoneDevice[]>([])
  const activeCallRef = useRef<TwilioCall | null>(null)
  const pendingCallRef = useRef<TwilioCall | null>(null)
  const previewCallRef = useRef<TwilioCall | null>(null)
  const [previewing, setPreviewing] = useState(false)
  // Hold (t1228). While a caller is held this browser has no Twilio leg at
  // all — the customer is parked in a conference — so 'holding' (the hold
  // request is in flight, our leg is about to drop) and 'held' both stop a
  // 'disconnect' from reading as the end of the call. `customerSidRef` is the
  // caller's own call id, kept once known: after a resume the caller is in a
  // conference rather than a child of our leg, so the server cannot find them
  // from our leg any more.
  const holdRef = useRef<'none' | 'holding' | 'held'>('none')
  const customerSidRef = useRef<string | null>(null)
  // Call waiting (t1300). `customerSidRef` / `heldNumberRef` / `parkedNumber`
  // describe the caller parked on hold; `activeCustomerSidRef` is the caller id
  // of the live leg once it has been resumed from hold, so a second call never
  // borrows the parked caller's id.
  const activeCustomerSidRef = useRef<string | null>(null)
  const heldNumberRef = useRef<string | null>(null)
  const [parkedNumber, setParkedNumber] = useState<string | null>(null)
  const [waiting, setWaiting] = useState<WaitingCall | null>(null)
  const waitingCallRef = useRef<TwilioCall | null>(null)
  // The live call we hung up to answer the waiting one — its 'disconnect' must
  // not reset the bar the new call now owns.
  const replacingRef = useRef<TwilioCall | null>(null)
  const queryClient = useQueryClient()
  // Read from long-lived Twilio handlers, so keep it in a ref (t1400).
  const queryClientRef = useRef(queryClient)
  queryClientRef.current = queryClient
  const stateRef = useRef<SoftphoneState>('idle')
  const remoteNumberRef = useRef<string | null>(null)
  stateRef.current = state
  remoteNumberRef.current = remoteNumber

  const clearParked = useCallback(() => {
    holdRef.current = 'none'
    customerSidRef.current = null
    heldNumberRef.current = null
    setParkedNumber(null)
  }, [])

  const attachCallHandlers = useCallback(
    (call: TwilioCall, direction: 'incoming' | 'outgoing', keepNumber = false, resumed = false) => {
      // A call started on another of this staff member's browsers (t1229)
      // arrives as an invite from our own number, with the customer's number
      // in `dialTo`.
      const dialTo = direction === 'incoming' ? call.customParameters?.get('dialTo') : undefined
      setRingingFromOtherDevice(Boolean(dialTo))
      if (!keepNumber) {
        setRemoteNumber(
          direction === 'incoming'
            ? (dialTo ?? call.parameters.From ?? 'Unknown number')
            : (call.parameters.To ?? null),
        )
      }
      // Desktop banner when the portal is in the background (t1383).
      if (direction === 'incoming' && !resumed) {
        const from = dialTo ?? call.parameters.From ?? 'Unknown number'
        void callerNotificationLabel(queryClientRef.current, from).then((label) => {
          // The call may have ended while the name was being looked up.
          if (pendingCallRef.current === call) void showIncomingCallNotification(label)
        })
      }
      call.on('accept', () => {
        void closeIncomingCallNotification()
        activeCallRef.current = call
        pendingCallRef.current = null
        if (resumed) {
          // Back from hold: the caller now lives in a conference, so keep their
          // id for the next hold/transfer.
          activeCustomerSidRef.current = customerSidRef.current
          clearParked()
        }
        setState('active')
        setMuted(false)
      })
      const onEnd = () => {
        void closeIncomingCallNotification()
        if (activeCallRef.current === call) activeCallRef.current = null
        if (pendingCallRef.current === call) pendingCallRef.current = null
        if (replacingRef.current === call) {
          replacingRef.current = null // ended to answer a waiting call
          return
        }
        if (activeCallRef.current || pendingCallRef.current) return // another call owns the bar
        setMuted(false)
        if (holdRef.current === 'holding') return // our leg dropping because we parked the caller
        if (holdRef.current === 'held') {
          // A failed resume, or the call that was answered over a held caller
          // ending: the caller is still on hold.
          setState('held')
          setRemoteNumber(heldNumberRef.current ?? remoteNumberRef.current)
          setRingingFromOtherDevice(false)
          return
        }
        customerSidRef.current = null
        activeCustomerSidRef.current = null
        setState('idle')
        setRemoteNumber(null)
        setRingingFromOtherDevice(false)
      }
      call.on('disconnect', onEnd)
      call.on('cancel', onEnd) // caller hung up, or another tab of ours answered first
      call.on('reject', onEnd)
      call.on('error', onEnd)
    },
    [clearParked],
  )

  useEffect(() => {
    if (!eligible) {
      setAvailable(false)
      setOtherDevices([])
      return
    }
    let cancelled = false
    let heartbeatTimer: ReturnType<typeof setInterval> | null = null

    void (async () => {
      let token: VoiceTokenResponse
      try {
        token = await api.post<VoiceTokenResponse>('/support/voice/token', {})
      } catch (err) {
        // Not configured (503) or not permitted (403) — stay dormant, no
        // error surfaced. A real outage retries on the next mount/tab.
        void err
        return
      }
      if (cancelled) return

      // A different staff member's identity than whatever Device is already
      // live (a shared terminal, a fast account switch) — start clean.
      if (device && deviceIdentity !== token.identity) {
        device.destroy()
        device = null
        deviceIdentity = null
      }

      if (!device) {
        const { Device } = await import('@twilio/voice-sdk')
        const newDevice = new Device(token.token, {
          codecPreferences: ['opus', 'pcmu'] as never,
          // Without this the SDK silently drops a second invite while a call is
          // live, so call waiting never gets to see it (t1300).
          allowIncomingWhileBusy: true,
        }) as unknown as TwilioDevice
        deviceIdentity = token.identity
        trackAudioContext(Device.audioContext)

        refreshTokenBeforeExpiry(newDevice)
        newDevice.on('incoming', (call) => {
          const incoming = call as TwilioCall
          // Started on a specific browser of ours (t1229): every browser
          // under this identity gets the invite, only the named one rings.
          const target = incoming.customParameters?.get('targetDevice')
          if (target && target !== thisDevice().key) {
            incoming.ignore()
            return
          }
          // This browser was told not to ring for customer calls (t1239);
          // the others of ours still do. Ignoring (not rejecting) leaves the
          // call ringing everywhere else.
          if (!target && !ringHereRef.current) {
            incoming.ignore()
            return
          }
          // Already ringing or dialing, or a call is already waiting: decline
          // so the ring list moves on to the next target.
          if (
            stateRef.current === 'connecting' ||
            pendingCallRef.current ||
            waitingCallRef.current
          ) {
            incoming.reject()
            return
          }
          // On a call, or with a caller on hold: call waiting (t1300). The
          // new call shows beside the current one instead of replacing it.
          if (activeCallRef.current || holdRef.current !== 'none') {
            const dialTo = incoming.customParameters?.get('dialTo')
            waitingCallRef.current = incoming
            setWaiting({
              remoteNumber: dialTo ?? incoming.parameters.From ?? 'Unknown number',
              fromOtherDevice: Boolean(dialTo),
            })
            void callerNotificationLabel(
              queryClientRef.current,
              dialTo ?? incoming.parameters.From ?? 'Unknown number',
            ).then((label) => {
              if (waitingCallRef.current === incoming) {
                void showIncomingCallNotification(label, true)
              }
            })
            const gone = () => {
              void closeIncomingCallNotification()
              if (waitingCallRef.current !== incoming) return
              waitingCallRef.current = null
              setWaiting(null)
            }
            for (const event of ['cancel', 'reject', 'disconnect', 'error']) {
              incoming.on(event, gone)
            }
            return
          }
          pendingCallRef.current = incoming
          setState('ringing')
          attachCallHandlers(incoming, 'incoming')
        })
        newDevice.on('error', () => {
          // A registration hiccup — not fatal to a call already in progress.
        })

        device = newDevice
      }

      if (cancelled) return
      setAvailable(true)
      // Register even without a browser ring target (t1229): inbound
      // routing only dials staff who have one, so this adds no inbound
      // ringing — it lets a call started on another browser reach this one.
      try {
        await device.register()
      } catch {
        // Registration can fail transiently (network, expired token on a
        // slow tab) — outbound calling still works either way.
      }
      if (cancelled) return

      const beat = () => {
        const me = thisDevice()
        void api
          .post<SoftphoneDevice[]>('/support/voice/devices/heartbeat', {
            deviceKey: me.key,
            label: me.label,
            ringIncoming: ringHereRef.current,
          })
          .then((devices) => {
            if (!cancelled) setOtherDevices(devices.filter((d) => d.deviceKey !== me.key))
          })
          .catch(() => {
            // Offline blip — the next beat retries; the list just goes stale.
          })
      }
      beatRef.current = beat
      beat()
      heartbeatTimer = setInterval(beat, HEARTBEAT_MS)
    })()

    return () => {
      cancelled = true
      if (heartbeatTimer) clearInterval(heartbeatTimer)
      beatRef.current = null
    }
    // Re-runs when the signed-in staff member changes; `attachCallHandlers`
    // is stable (useCallback with no deps that matter here).
  }, [eligible, attachCallHandlers])

  // Unlock sound on any tap or key press, for the life of the page: Safari
  // suspends the context again after the tab is backgrounded or the screen
  // locks. Passive and never prevented, so it can't interfere with clicks.
  useEffect(() => {
    if (!eligible) return
    const sync = () => setSoundBlocked(audioCtx ? audioCtx.state !== 'running' : false)
    audioListeners.add(sync)
    sync()
    const events = ['pointerdown', 'touchend', 'keydown'] as const
    const options = { capture: true, passive: true }
    for (const name of events) document.addEventListener(name, unlockAudio, options)
    return () => {
      audioListeners.delete(sync)
      for (const name of events) document.removeEventListener(name, unlockAudio, options)
    }
  }, [eligible])

  const ensureDevice = useCallback(async () => {
    if (!device) {
      // Lazily stand up a Device for a staff member with no browser ring
      // target — outbound calling doesn't require registering for inbound.
      const token = await api.post<VoiceTokenResponse>('/support/voice/token', {})
      const { Device } = await import('@twilio/voice-sdk')
      device = new Device(token.token, {
        codecPreferences: ['opus', 'pcmu'] as never,
        // Without this the SDK silently drops a second invite while a call is
        // live, so call waiting never gets to see it (t1300).
        allowIncomingWhileBusy: true,
      }) as unknown as TwilioDevice
      trackAudioContext(Device.audioContext)
      refreshTokenBeforeExpiry(device)
      deviceIdentity = token.identity
      setAvailable(true)
    }
    return device
  }, [])

  const ensurePreviewDevice = useCallback(async () => {
    const token = await api.post<VoiceTokenResponse>('/support/voice/token', {})
    if (previewDevice && previewIdentity !== token.identity) {
      previewDevice.destroy()
      previewDevice = null
      previewIdentity = null
    }
    if (!previewDevice) {
      const { Device } = await import('@twilio/voice-sdk')
      previewDevice = new Device(token.token, {
        codecPreferences: ['opus', 'pcmu'] as never,
        // Replace the microphone with silence — a preview only listens.
        getUserMedia: async () => {
          const ctx = Device.audioContext ?? new AudioContext()
          return ctx.createMediaStreamDestination().stream
        },
      }) as unknown as TwilioDevice
      previewIdentity = token.identity
      trackAudioContext(Device.audioContext)
      refreshTokenBeforeExpiry(previewDevice)
    } else {
      previewDevice.updateToken(token.token)
    }
    return previewDevice
  }, [])

  const placeCall = useCallback(
    async (to: string) => {
      const dev = await ensureDevice()
      setState('connecting')
      const call = await dev.connect({ params: { To: to } })
      attachCallHandlers(call, 'outgoing')
    },
    [attachCallHandlers, ensureDevice],
  )

  const stopIvrKeyPreview = useCallback(() => {
    previewCallRef.current?.disconnect()
  }, [])

  const playIvrKeyPreview = useCallback(
    async (
      key:
        | { digit: string; label: string; message: string | null }
        | { voice: string },
      onError?: (message: string) => void,
    ) => {
      if (previewCallRef.current) throw new Error('A preview is already playing.')
      if (state !== 'idle' || activeCallRef.current || pendingCallRef.current)
        throw new Error('Finish the current call before listening to a key.')
      unlockAudio()
      setPreviewing(true)
      try {
        const dev = await ensurePreviewDevice()
        const call = await dev.connect({
          params:
            'voice' in key
              ? { PreviewVoice: key.voice }
              : {
                  PreviewIvrKey: '1',
                  PreviewDigit: key.digit,
                  PreviewLabel: key.label,
                  PreviewMessage: key.message ?? '',
                },
        })
        previewCallRef.current = call
        const done = () => {
          if (previewCallRef.current === call) previewCallRef.current = null
          setPreviewing(false)
        }
        call.on('disconnect', done)
        call.on('cancel', done)
        call.on('error', (err: unknown) => {
          done()
          onError?.(err instanceof Error && err.message ? err.message : 'The preview was cut off.')
        })
      } catch (err) {
        previewCallRef.current = null
        setPreviewing(false)
        throw err
      }
    },
    [state, ensurePreviewDevice],
  )

  const placeCallOnDevice = useCallback(async (to: string, deviceKey: string) => {
    await api.post('/support/voice/device-calls', { to, deviceKey })
  }, [])

  const setRingHere = useCallback((on: boolean) => {
    ringHereRef.current = on
    setRingHereState(on)
    unlockAudio()
    try {
      localStorage.setItem(RING_STORAGE_KEY, String(on))
    } catch {
      // Storage blocked — the choice lasts until this tab reloads.
    }
    // Tell the server now so it can skip us if every browser is silenced.
    beatRef.current?.()
  }, [])

  const silence = useCallback(() => {
    pendingCallRef.current?.ignore()
    pendingCallRef.current = null
    setState('idle')
    setRemoteNumber(null)
    setRingingFromOtherDevice(false)
  }, [])

  const answer = useCallback(() => pendingCallRef.current?.accept(), [])
  const reject = useCallback(() => {
    pendingCallRef.current?.reject()
    pendingCallRef.current = null
    setState('idle')
    setRemoteNumber(null)
    setRingingFromOtherDevice(false)
  }, [])
  const hangUp = useCallback(() => {
    // A live call ends on its own, leaving any caller on hold parked.
    if (activeCallRef.current) {
      activeCallRef.current.disconnect()
      return
    }
    if (holdRef.current === 'held') {
      const sid = customerSidRef.current
      clearParked()
      setState('idle')
      setRemoteNumber(null)
      if (sid) {
        void api.post('/support/voice/hold/end', { customerCallSid: sid }).catch(() => {
          // The caller may already have hung up — nothing left to end.
        })
      }
      return
    }
  }, [clearParked])

  /** Whichever id the server needs to find the caller: ours, until a hold or
   *  resume has taught us theirs. */
  const callIds = useCallback((): { callSid: string } | { customerCallSid: string } => {
    // The live leg's caller if we know it (after a resume), else our own leg;
    // with no live leg, the caller on hold.
    const live = activeCallRef.current
    if (live && activeCustomerSidRef.current)
      return { customerCallSid: activeCustomerSidRef.current }
    if (!live && customerSidRef.current) return { customerCallSid: customerSidRef.current }
    const sid = live?.parameters.CallSid
    if (!sid) throw new Error('This call has no id yet — try again in a moment.')
    return { callSid: sid }
  }, [])

  const hold = useCallback(async () => {
    if (!activeCallRef.current) return
    const ids = callIds()
    holdRef.current = 'holding'
    try {
      const res = await api.post<HoldCallResponse>('/support/voice/hold', ids)
      customerSidRef.current = res.customerCallSid
      activeCustomerSidRef.current = null
      heldNumberRef.current = remoteNumberRef.current
      setParkedNumber(remoteNumberRef.current)
      activeCallRef.current = null // our leg is dropping; the caller is parked
      holdRef.current = 'held'
      setState('held')
    } catch (err) {
      holdRef.current = 'none'
      throw err
    }
  }, [callIds])

  const resume = useCallback(async () => {
    const sid = customerSidRef.current
    if (holdRef.current !== 'held' || !sid || !device || activeCallRef.current) return
    setState('connecting')
    try {
      const call = await device.connect({ params: { ResumeCall: sid } })
      attachCallHandlers(call, 'outgoing', true, true)
    } catch (err) {
      setState('held')
      throw err
    }
  }, [attachCallHandlers])

  const transfer = useCallback(
    async (targetId: string, announce: boolean) => {
      if (state !== 'active' && state !== 'held') return
      const hadLive = Boolean(activeCallRef.current)
      await api.post('/support/voice/transfer', { ...callIds(), targetId, announce })
      // The caller now belongs to the target; our leg (if any) is released.
      activeCustomerSidRef.current = null
      setMuted(false)
      if (hadLive && holdRef.current === 'held') {
        // The transferred call was the one over a caller on hold; that caller
        // stays parked.
        activeCallRef.current = null
        setState('held')
        setRemoteNumber(heldNumberRef.current)
        return
      }
      clearParked()
      setState('idle')
      setRemoteNumber(null)
    },
    [state, callIds, clearParked],
  )
  const toggleMute = useCallback(() => {
    const call = activeCallRef.current
    if (!call) return
    const next = !call.isMuted()
    call.mute(next)
    setMuted(next)
  }, [])

  // A caller who hangs up while on hold gives this browser no signal (it has no
  // leg); the server drops everyone on hold after ten minutes, so stop showing
  // the hold bar then too.
  useEffect(() => {
    if (state !== 'held') return
    const id = setTimeout(
      () => {
        clearParked()
        setState('idle')
        setRemoteNumber(null)
      },
      10 * 60_000 + 5_000,
    )
    return () => clearTimeout(id)
  }, [state, clearParked])

  const declineWaiting = useCallback(() => {
    waitingCallRef.current?.reject()
    waitingCallRef.current = null
    setWaiting(null)
  }, [])

  const answerWaiting = useCallback(
    async (mode: 'hold' | 'end') => {
      const call = waitingCallRef.current
      if (!call) return
      const live = activeCallRef.current
      if (live) {
        if (mode === 'hold') {
          if (holdRef.current !== 'none') {
            throw new Error('Only one caller can be on hold at a time.')
          }
          await hold()
        } else {
          replacingRef.current = live
          live.disconnect()
        }
      } else if (mode === 'end' && holdRef.current === 'held') {
        const sid = customerSidRef.current
        clearParked()
        if (sid) void api.post('/support/voice/hold/end', { customerCallSid: sid }).catch(() => {})
      }
      // The caller may have hung up while we were parking the first one.
      if (waitingCallRef.current !== call) return
      waitingCallRef.current = null
      setWaiting(null)
      pendingCallRef.current = call
      attachCallHandlers(call, 'incoming')
      call.accept()
    },
    [hold, clearParked, attachCallHandlers],
  )

  // The SDK plays no ringtone for a call that arrives mid-call, so beep twice
  // every few seconds through the page's audio context (sound unlock: t1291).
  useEffect(() => {
    if (!waiting || state !== 'active') return
    const beep = () => {
      const ctx = audioCtx
      if (!ctx || ctx.state !== 'running') return
      for (const offset of [0, 0.25]) {
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.frequency.value = 880
        gain.gain.value = 0.08
        osc.connect(gain)
        gain.connect(ctx.destination)
        osc.start(ctx.currentTime + offset)
        osc.stop(ctx.currentTime + offset + 0.15)
      }
    }
    beep()
    const id = setInterval(beep, 4000)
    return () => clearInterval(id)
  }, [waiting, state])

  return (
    <SoftphoneContext.Provider
      value={{
        available,
        state,
        remoteNumber,
        ringingFromOtherDevice,
        otherDevices,
        muted,
        ringHere,
        setRingHere,
        soundBlocked,
        thisDeviceLabel: thisDevice().label,
        waiting,
        parkedNumber,
        answerWaiting,
        declineWaiting,
        placeCall,
        playIvrKeyPreview,
        stopIvrKeyPreview,
        previewing,
        placeCallOnDevice,
        answer,
        reject,
        silence,
        hangUp,
        toggleMute,
        hold,
        resume,
        transfer,
      }}
    >
      {children}
    </SoftphoneContext.Provider>
  )
}
